import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { getHHMailTemplates, getHHActiveVacancies, sendHHInvitation } from './services/api';
import { UniversalDropdownWithLabel } from '../components/ui/universal-dropdown';
import { useBrandingColors } from '../hooks/useBrandingColors';

const EmailDialog = ({ open, onClose, selectedCandidates, sourcePositionId, vacancyTitle }) => {
  const colors = useBrandingColors();
  const [loading, setLoading] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [vacanciesLoading, setVacanciesLoading] = useState(false);
  
  const [templates, setTemplates] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedVacancy, setSelectedVacancy] = useState('');
  const [emailMessage, setEmailMessage] = useState('');

  // Load templates and vacancies when dialog opens
  useEffect(() => {
    if (open) {
      loadTemplatesAndVacancies();
    } else {
      // Reset form when dialog closes
      setSelectedTemplate(null);
      setSelectedVacancy('');
      setEmailMessage('');
      setTemplates([]);
      setVacancies([]);
    }
  }, [open]);

  // Auto-select invite_mail template when templates are loaded
  useEffect(() => {
    if (templates.length > 0 && !selectedTemplate) {
      const inviteTemplate = templates.find(t => t.id === 'invite_mail');
      if (inviteTemplate) {
        setSelectedTemplate(inviteTemplate);
        setEmailMessage(inviteTemplate.text || '');
      }
    }
  }, [templates, selectedTemplate]);

  const loadTemplatesAndVacancies = async () => {
    try {
      setTemplatesLoading(true);
      setVacanciesLoading(true);
      
      const [templatesResponse, vacanciesResponse] = await Promise.all([
        getHHMailTemplates(),
        getHHActiveVacancies()
      ]);
      
      const templatesData = (templatesResponse && templatesResponse.templates !== undefined)
        ? templatesResponse.templates
        : templatesResponse;
      const vacanciesRaw = (vacanciesResponse && vacanciesResponse.vacancies !== undefined)
        ? vacanciesResponse.vacancies
        : vacanciesResponse;

      // Vacancies can come as an array or as an object with items
      const vacanciesArray = Array.isArray(vacanciesRaw)
        ? vacanciesRaw
        : (Array.isArray(vacanciesRaw?.items) ? vacanciesRaw.items : []);

      setTemplates(Array.isArray(templatesData) ? templatesData : []);
      setVacancies(vacanciesArray);
      
    } catch (error) {
      console.error('Error loading templates and vacancies:', error);
      toast.error('Ошибка загрузки данных из HH.ru');
    } finally {
      setTemplatesLoading(false);
      setVacanciesLoading(false);
    }
  };

  const handleTemplateChange = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    setSelectedTemplate(template);
    setEmailMessage(template?.text || '');
  };

  const handleSend = async () => {
    if (!selectedVacancy || !selectedTemplate) {
      toast.error('Выберите вакансию и шаблон письма');
      return;
    }

    if (!emailMessage.trim()) {
      toast.error('Введите текст сообщения');
      return;
    }

    setLoading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Send invitations one by one to each candidate
      for (const candidate of selectedCandidates) {
        try {
          await sendHHInvitation({
            resumeId: candidate.id,
            vacancyId: selectedVacancy,
            templateId: selectedTemplate.id,
            message: emailMessage
          });
          successCount++;
        } catch (error) {
          console.error(`Error sending invitation to candidate ${candidate.id}:`, error);
          errorCount++;
        }
      }
      
      if (errorCount > 0) {
        toast.error(`Отправлено приглашений: ${successCount}, ошибок: ${errorCount}`);
      } else {
        toast.success(`Успешно отправлено ${successCount} приглашений через HH.ru`);
      }
      
      onClose();
      
    } catch (error) {
      toast.error('Ошибка при отправке приглашений');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-2xl w-full bg-white rounded-xl shadow-xl">
          <div className="flex items-center justify-between p-6 border-b">
            <Dialog.Title className="text-lg font-semibold">
              Отправка приглашений через HH.ru
            </Dialog.Title>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6">
            {Boolean(sourcePositionId) && (
              <div className="mb-6 rounded-2xl border border-[#e2e4e9] bg-[#f6f8fa] p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="text-sm text-[#525866]">Выбранная вакансия</div>
                    <div className="mt-0.5 text-[16px] text-[#000000] font-medium truncate" title={vacancyTitle || ''}>
                      {vacancyTitle || `Вакансия #${sourcePositionId}`}
                    </div>
                  </div>
                  <InterviewLinkBadge positionId={sourcePositionId} />
                </div>
              </div>
            )}
            <div className="rounded-2xl p-4 mb-6" style={{ backgroundColor: 'var(--brand-primary)', opacity: 0.9, border: '1px solid var(--brand-primary)' }}>
              <p className="text-sm text-black">
                Будет отправлено {selectedCandidates.length} приглашений через HH.ru API (с SMS уведомлением)
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Vacancy Selection */}
              <div>
                {vacanciesLoading ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Вакансия</label>
                    <div className="w-full px-[12px] py-[12px] border rounded-[12px] flex items-center h-[42px]" style={{ borderColor: 'var(--border-color)' }}>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 mr-2" style={{ borderBottomColor: 'var(--brand-primary)' }}></div>
                      Загрузка вакансий...
                    </div>
                  </div>
                ) : (
                  <UniversalDropdownWithLabel
                    label="Вакансия на HH"
                    value={selectedVacancy}
                    onChange={(value) => setSelectedVacancy(String(value))}
                    options={vacancies.map((vacancy) => ({
                      value: vacancy.id,
                      label: `${vacancy.name}${vacancy.area ? ` (${vacancy.area.name || vacancy.area})` : ''}`
                    }))}
                    placeholder="Выберите вакансию"
                    required
                  />
                )}
              </div>

              {/* Template Selection */}
              <div>
                {templatesLoading ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Шаблон письма</label>
                    <div className="w-full px-[12px] py-[12px] border rounded-[12px] flex items-center h-[42px]" style={{ borderColor: 'var(--border-color)' }}>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 mr-2" style={{ borderBottomColor: 'var(--brand-primary)' }}></div>
                      Загрузка шаблонов...
                    </div>
                  </div>
                ) : (
                  <UniversalDropdownWithLabel
                    label="Шаблон письма"
                    value={selectedTemplate?.id || ''}
                    onChange={(value) => handleTemplateChange(String(value))}
                    options={templates.map((template) => ({ value: template.id, label: template.name }))}
                    placeholder="Выберите шаблон"
                    required
                  />
                )}
              </div>
            </div>

            {/* Message Text */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Текст сообщения:
              </label>
              <textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                className="w-full px-[12px] py-[12px] border rounded-[12px] focus:outline-none min-h-[250px] font-mono text-sm resize-y"
                style={{ borderColor: 'var(--border-color)' }}
                placeholder="Выберите шаблон или введите текст сообщения..."
                rows={12}
                required
              />
              <p className="mt-2 text-xs text-gray-500">
                Используйте переменные из шаблона HH.ru (например, [Name], [Company], [Vacancy])
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 p-6 border-t">
            <button
              onClick={onClose}
              disabled={loading}
              className={`bg-white border border-[#cdd0d5] text-[#525866] font-medium box-border content-stretch flex items-center justify-center px-[32px] py-[14px] rounded-[12px] transition-colors duration-200 h-11 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={{ color: '#525866' }}
              onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.color = colors.primary; e.currentTarget.style.borderColor = colors.primary; } }}
              onMouseLeave={(e) => { if (!loading) { e.currentTarget.style.color = '#525866'; e.currentTarget.style.borderColor = '#cdd0d5'; } }}
            >
              Отмена
            </button>
            <button
              onClick={handleSend}
              disabled={loading || !selectedVacancy || !selectedTemplate || !emailMessage.trim()}
              className={`box-border content-stretch flex gap-[8px] items-center px-[32px] py-[14px] relative rounded-[12px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] shrink-0 text-white transition-colors h-11 ${
                (loading || !selectedVacancy || !selectedTemplate || !emailMessage.trim()) ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => { if (!loading && selectedVacancy && selectedTemplate && emailMessage.trim()) { e.currentTarget.style.backgroundColor = colors.primaryHover; } }}
              onMouseLeave={(e) => { if (!loading && selectedVacancy && selectedTemplate && emailMessage.trim()) { e.currentTarget.style.backgroundColor = colors.primary; } }}
            >
              {loading ? 'Отправка...' : 'Отправить приглашения'}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

// Небольшой бэйдж со ссылкой «Ссылка на интервью» с копированием (в правой части шапки блока)
const InterviewLinkBadge = ({ positionId }) => {
  const colors = useBrandingColors();
  const candidateBaseUrl = process.env.REACT_APP_CANDIDATE_BASE_URL || '';
  const link = `${candidateBaseUrl}/candidate/interview/${positionId}`;
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Ссылка скопирована');
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = link;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        toast.success('Ссылка скопирована');
      } catch {}
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center gap-2 rounded-3xl bg-white border border-[#e2e4e9] text-[#525866] px-3 py-2 text-sm transition"
      style={{ color: '#525866' }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = colors.primary; e.currentTarget.style.color = colors.primary; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e4e9'; e.currentTarget.style.color = '#525866'; }}
      title="Скопировать ссылку на интервью"
    >
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 010 5.656l-2 2a4 4 0 11-5.656-5.656l1-1M10.172 13.828a4 4 0 010-5.656l2-2a4 4 0 115.656 5.656l-1 1"/>
      </svg>
      Ссылка на интервью
    </button>
  );
};

export default EmailDialog;